import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { COLORS } from '../_shared/tokens';
import mobilizeiIcon from '../../../assets/img/previewer_icon_ios.png';
import ecommercePlatforms from '../../../utils/ecommercePlatforms';
import config from '../../../utils/config';
import { AUTH_ORIGINS, PLATFORMS, mixpanelEvents } from '../../../utils/constants';
import { useOnboarding } from '../../../contexts/onboarding.context';
import { authIrrobaService } from '../../../services/irroba/auth.service';
import { showApiError } from '../../../utils/errors';
import { isValidUrl, normalizeUrl } from '../../../utils/url';
import { storePendingPlatformAuth, trackPlatformAuthFailure } from '../../../utils/platformAuth';
import { MbTextField } from '../../../components/MbTextFields';
import mixpanel from 'mixpanel-browser';

function Chip({ children, label }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            <Box
                sx={{
                    width: 74,
                    height: 74,
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.07)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                }}
            >
                {children}
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: COLORS.textMuted }}>{label}</Typography>
        </Box>
    );
}

/** Mobilizei ↔ Platorm connection visual. */
function ConnectVisual({ platform }) {

    const platformLogo = require(`../../../assets/img/platforms/${platform?.id}.png`)

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
            <Chip>
                <Box component="img" src={mobilizeiIcon} alt="" sx={{ width: 55, objectFit: 'contain' }} />
            </Chip>

            <Box sx={{ position: 'relative', width: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2}}>
                <Box
                    sx={{
                        position: 'absolute',
                        left: 4,
                        right: 4,
                        borderTop: '2px dashed rgba(0,0,0,0.16)'
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: COLORS.green,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 3px 10px rgba(60,213,104,0.4)',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            backgroundColor: COLORS.green,
                            animation: 'nuvemPulse 2s ease-out infinite'
                        },
                        '@keyframes nuvemPulse': {
                            '0%, 100%': { transform: 'scale(1)', opacity: 0.55 },
                            '50%': { transform: 'scale(1.35)', opacity: 0 }
                        }
                    }}
                >
                    <SyncAltIcon sx={{ fontSize: 19, color: '#fff', position: 'relative' }} />
                </Box>
            </Box>

            <Chip >
                <Box component="img" src={platformLogo} alt="" sx={{ width: 62, objectFit: 'contain' }} />
            </Chip>
        </Box>
    );
}

const AUTH_URLS = {
    [PLATFORMS.NUVEMSHOP]: config.NUVEMSHOP_AUTH_URL,
    [PLATFORMS.WIX]: config.WIX_AUTH_URL
};

const PLATFORMS_WITH_AUTH_STATE = [PLATFORMS.NUVEMSHOP];

/**
 * Tray and Bagy authorize on the store's own admin, so the url is built from the
 * store url. Tray keeps sending the same code to a callback url that has already
 * received one, even after the app is uninstalled and that code stops working,
 * so each attempt gets its own callback url and a new code.
 */
function buildTrayCompatibleAuthUrl(platformId, storeUrl) {
    const profile = ecommercePlatforms.getProfileById(platformId);
    const callback = `${config[profile.authCallbackKey]}?attempt=${Date.now()}`;

    return `${normalizeUrl(storeUrl)}/auth.php?response_type=code` +
        `&consumer_key=${profile.oauthConsumerKey}` +
        `&callback=${encodeURIComponent(callback)}`;
}

function buildAuthUrl(platformId, storeUrl, authOrigin) {
    if (ecommercePlatforms.getProfileById(platformId)) {
        return buildTrayCompatibleAuthUrl(platformId, storeUrl);
    }

    const authUrl = AUTH_URLS[platformId];

    if (!authUrl || !PLATFORMS_WITH_AUTH_STATE.includes(platformId)) {
        return authUrl;
    }

    const url = new URL(authUrl);
    url.searchParams.set('state', authOrigin);

    return url.toString();
}

/**
 * Irroba has no OAuth consent: the store is reached through an API user that the
 * merchant creates on his own panel, so the credentials are asked for here.
 * Same call as the one on components/platformIntegrations/IrrobaIntegration.
 */
function IrrobaForm({ storeUrl, onRedirecting }) {
    const { data } = useOnboarding();
    const [isLoading, setIsLoading] = useState(false);

    const { control, handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: { storeId: '', storeUrl: storeUrl || '', username: '', password: '' }
    });

    const onSubmit = (values) => {
        setIsLoading(true);

        mixpanel.track(mixpanelEvents.ONBOARDING_PLATFORM_AUTH_INITIATED, {
            'platform_id': PLATFORMS.IRROBA
        })

        storePendingPlatformAuth(PLATFORMS.IRROBA);

        return authIrrobaService.authenticate(
            values.username,
            values.password,
            values.storeId,
            data.storeName,
            normalizeUrl(values.storeUrl)
        )
            .then((response) => {
                if (response.urlToRedirect) {
                    onRedirecting();
                    window.open(response.urlToRedirect, "_self");
                }
            })
            .catch((e) => {
                trackPlatformAuthFailure({
                    platformId: PLATFORMS.IRROBA,
                    failureReason: e?.error?.message
                });

                showApiError(e.error);
                setIsLoading(false);
            })
    }

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: COLORS.textMuted, mb: 2 }}>
                Crie um usuário de API no painel da Irroba e preencha os dados abaixo.
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
                <Controller
                    name="storeId"
                    control={control}
                    rules={{ required: "O ID da loja é obrigatório." }}
                    render={({ field }) => (
                        <MbTextField
                            label="ID da loja"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.storeId?.message}
                        />
                    )}
                />
                <Controller
                    name="storeUrl"
                    control={control}
                    rules={{
                        required: "A URL da loja é obrigatória.",
                        validate: (value) => isValidUrl(value) || "Parece que tem algo de errado com esta url! Verifique se ela coincide com a URL da sua loja Irroba."
                    }}
                    render={({ field }) => (
                        <MbTextField
                            label="URL da loja"
                            inputMode="url"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={() => {
                                field.onBlur();
                                const normalized = normalizeUrl(field.value);
                                if (normalized) setValue('storeUrl', normalized, { shouldValidate: true });
                            }}
                            error={errors.storeUrl?.message}
                        />
                    )}
                />
                <Controller
                    name="username"
                    control={control}
                    rules={{ required: "O usuário de API é obrigatório." }}
                    render={({ field }) => (
                        <MbTextField
                            label="Usuário de API"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.username?.message}
                        />
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    rules={{ required: "A senha do usuário de API é obrigatória." }}
                    render={({ field }) => (
                        <MbTextField
                            label="Senha do usuário de API"
                            type="password"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.password?.message}
                        />
                    )}
                />
            </Stack>

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                sx={{ py: 2, fontSize: 16 }}
            >
                {isLoading ? 'conectando...' : 'conectar minha loja'}
            </Button>
        </Box>
    );
}

/**
 * OAuth modal shown over D0c when the store is on Nuvemshop, Tray, Bagy, Irroba or Wix. It is an overlay,
 * not a new step: it does not consume a point on the progress bar.
 *
 * Also used on the dashboard by stores that have to authorize the app again. The
 * authOrigin tells the API where the authorization started (only Nuvemshop sends
 * it), and the texts can be replaced to explain why it is asked for.
 */
export default function AuthorizationModal({
    open,
    platformId,
    redirecting,
    storeUrl,
    onRedirecting,
    onClose,
    authOrigin = AUTH_ORIGINS.ONBOARDING,
    title,
    description,
    permissionNote
}) {

    const platform = ecommercePlatforms.getById(platformId)

    const isIrroba = platformId === PLATFORMS.IRROBA;

    const handleAuthorize = () => {
        mixpanel.track(mixpanelEvents.ONBOARDING_PLATFORM_AUTH_INITIATED, {
            'platform_id': platformId
        })

        // The url is kept to retry the authorization in case the platform sends
        // the merchant back without it (see retryPendingTrayCompatibleAuth)
        const authUrl = buildAuthUrl(platformId, storeUrl, authOrigin);

        storePendingPlatformAuth(platformId, authUrl);

        onRedirecting();
        window.open(authUrl, "_self");
    }

    return (
        <Dialog
            open={open}
            onClose={redirecting ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '20px', p: { xs: 3, md: '32px 40px 34px' }, maxWidth: 508 } }}
        >
            <IconButton
                onClick={onClose}
                disabled={redirecting}
                aria-label="fechar"
                sx={{ display: { xs: 'none', md: 'inline-flex' }, position: 'absolute', top: 16, right: 16, color: COLORS.textMuted }}
            >
                <CloseIcon />
            </IconButton>

            <ConnectVisual platform={platform} />

            <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: 22, md: 26 }, lineHeight: 1.2, mt: 2.75, mb: 1.25 }}>
                {title || `Vamos conectar sua loja ${platform?.name}`}
            </Typography>

            <Typography sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.55, color: COLORS.textMuted }}>
                {description || (isIrroba
                    ? 'Nos conectamos à sua loja através da criação de um usuário de API.'
                    : `Você vai para o painel da ${platform?.name} para autorizar a Mobilizei. A autorização é rápida e depois voltamos para cá automaticamente.`)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', backgroundColor: '#f6f7f6', borderRadius: '12px', p: '13px 15px', my: 3 }}>
                <ShieldOutlinedIcon sx={{ fontSize: 19, color: COLORS.forest, mt: '1px', flex: 'none' }} />
                <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(0,0,0,0.62)' }}>
                    {permissionNote || (isIrroba
                        ? 'O usuário de API dá acesso à sua loja. Usamos esse acesso para consultar suas informações e criar seu app. '
                        : `A ${platform?.name} vai pedir permissão de acesso à sua loja. Usamos esse acesso para consultar suas informações e criar seu app. `)}
                    <Box component="span" sx={{ color: '#000', fontWeight: 600 }}>
                        Não alteramos seus produtos, preços ou pedidos.
                    </Box>
                </Typography>
            </Box>

            {redirecting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, height: 54 }}>
                    <CircularProgress size={19} thickness={5} />
                    <Typography sx={{ fontWeight: 600, fontSize: 16 }}>redirecionando para a {platform?.name}...</Typography>
                </Box>
            ) : isIrroba ? (
                <IrrobaForm storeUrl={storeUrl} onRedirecting={onRedirecting} />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.75 }}>
                    <Button fullWidth variant="contained" onClick={handleAuthorize} endIcon={<ArrowForwardIcon />} sx={{ py: 2, fontSize: { xs: 14, md: 16 } }}>
                        autorizar na {platform?.name}
                    </Button>
                    <Button variant="text" color="secondary" onClick={onClose} sx={{ fontWeight: 600, color: COLORS.textMuted }}>
                        cancelar
                    </Button>
                </Box>
            )}
        </Dialog>
    );
}
